import { motion } from "framer-motion";
import { ChevronLeft, User, Users, ArrowRight, ClipboardList, ShieldCheck, Clock3, Sparkles } from "lucide-react";

export default function RegistrationTypeChoice({ eventTitle, onSelectSingle, onSelectGroup, onBack }) {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header – below fixed navbar */}
      <header className="sticky top-16 z-30 mt-16 border-b border-white/70 bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/75 sm:top-20 sm:mt-20">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-full bg-gradient-to-r from-amber-500/[0.12] via-orange-500/[0.08] to-transparent" aria-hidden />
        <div className="relative mx-auto flex max-w-5xl flex-col gap-3 px-4 py-3 sm:gap-4 sm:px-6 sm:py-4 lg:flex-row lg:items-center lg:px-8">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-600">
              <ClipboardList size={20} strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <div className="mb-1">
                <span className="inline-flex items-center rounded-full border border-amber-200/70 bg-amber-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                  Registration Setup
                </span>
              </div>
              <h1 className="text-base font-bold tracking-tight text-gray-900 sm:text-xl">
                Choose registration type
              </h1>
              {eventTitle ? (
                <p className="text-xs font-medium text-gray-600">{eventTitle}</p>
              ) : (
                <p className="text-xs font-medium text-gray-600">Single or group</p>
              )}
            </div>
          </div>
          <div className="flex w-full shrink-0 items-center gap-2 lg:w-auto">
            <motion.button
              type="button"
              onClick={onBack}
              whileHover={{ x: -2 }}
              whileTap={{ scale: 0.98 }}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200/80 bg-white/90 px-3 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-amber-500/10 hover:text-amber-800 sm:flex-none"
            >
              <ChevronLeft size={20} strokeWidth={2} />
              <span className="sm:hidden">Back</span>
              <span className="hidden sm:inline">Back to event</span>
            </motion.button>
          </div>
        </div>
      </header>

      <div className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <div className="mb-8 rounded-2xl border border-gray-200/80 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <span className="inline-flex items-center gap-2 rounded-full bg-green-50 px-3 py-1.5 text-xs font-medium text-green-700">
                <ShieldCheck size={14} />
                Secure registration flow
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700">
                <Clock3 size={14} />
                Takes less than 3 minutes
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-violet-50 px-3 py-1.5 text-xs font-medium text-violet-700">
                <Sparkles size={14} />
                You can edit details later
              </span>
            </div>
            <p className="mt-4 text-sm text-gray-600">
              Select the registration path that matches your attendee setup. You can proceed immediately after choosing.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2 md:gap-5">
            {/* Single */}
            <motion.button
              type="button"
              onClick={onSelectSingle}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.995 }}
              className="group relative overflow-hidden rounded-2xl border border-gray-200/90 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-orange-300 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 sm:p-6"
            >
              <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-r from-orange-500/15 to-transparent opacity-80" aria-hidden />
              <div className="relative flex h-full flex-col">
                <div className="mb-5 flex items-start justify-between gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-orange-600 transition-colors group-hover:bg-orange-200 sm:h-14 sm:w-14">
                    <User size={24} className="sm:h-7 sm:w-7" />
                  </div>
                  <span className="inline-flex items-center rounded-full bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-700">
                    Most common
                  </span>
                </div>

                <h2 className="text-lg font-semibold text-gray-900">Single registration</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Register one attendee quickly, then choose to pay now or complete payment later.
                </p>

                <div className="mt-5 space-y-2 text-sm text-gray-600">
                  <p className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
                    Ideal for individual participants
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
                    Fastest registration path
                  </p>
                </div>

                <div className="mt-6 flex items-center justify-between text-sm font-semibold text-gray-900">
                  <span>Continue as single</span>
                  <ArrowRight size={20} className="text-gray-400 transition-transform group-hover:translate-x-1 group-hover:text-orange-500" />
                </div>
              </div>
            </motion.button>

            {/* Group */}
            <motion.button
              type="button"
              onClick={onSelectGroup}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.995 }}
              className="group relative overflow-hidden rounded-2xl border border-gray-200/90 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-teal-300 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 sm:p-6"
            >
              <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-r from-teal-500/15 to-transparent opacity-80" aria-hidden />
              <div className="relative flex h-full flex-col">
                <div className="mb-5 flex items-start justify-between gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-teal-100 text-teal-600 transition-colors group-hover:bg-teal-200 sm:h-14 sm:w-14">
                    <Users size={24} className="sm:h-7 sm:w-7" />
                  </div>
                  <span className="inline-flex items-center rounded-full bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-700">
                    Team option
                  </span>
                </div>

                <h2 className="text-lg font-semibold text-gray-900">Group registration</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Register multiple attendees for one organization using one guided flow and payment path.
                </p>

                <div className="mt-5 space-y-2 text-sm text-gray-600">
                  <p className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />
                    Add attendees in one session
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />
                    Built for schools, teams, and offices
                  </p>
                </div>

                <div className="mt-6 flex items-center justify-between text-sm font-semibold text-gray-900">
                  <span>Continue as group</span>
                  <ArrowRight size={20} className="text-gray-400 transition-transform group-hover:translate-x-1 group-hover:text-teal-500" />
                </div>
              </div>
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  );
}
